import { Add as AddIcon, Remove } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import { formatMoney } from 'lib/functions/utils';
import { BankAccount, PayoutDistribution } from 'lib/types/models';
import React, { useEffect, useState } from 'react';

interface EditPayoutPlanDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  payoutPlan: PayoutDistribution[];
  setPayoutPlan: (plan: PayoutDistribution[]) => void;
  operationToDeposit: number | null;
  selectableBankAccounts: BankAccount[];
}

const EditPayoutPlanDialog: React.FC<EditPayoutPlanDialogProps> = ({
  open,
  setOpen,
  payoutPlan,
  setPayoutPlan,
  operationToDeposit,
  selectableBankAccounts,
}) => {
  if (!operationToDeposit) return null;
  const [localPayoutPlan, setLocalPayoutPlan] = useState<PayoutDistribution[]>(
    [],
  );

  useEffect(() => {
    if (open) {
      setLocalPayoutPlan([...payoutPlan]);
    }
  }, [open, payoutPlan]);

  const getAccumulatedAmount = () => {
    return localPayoutPlan.reduce((acc, p) => acc + p.amount, 0);
  };

  const getRemainingAmount = () => {
    return operationToDeposit - getAccumulatedAmount();
  };

  const handleAddDistribution = () => {
    if (selectableBankAccounts.length === 0) return;

    const firstBankAccount = selectableBankAccounts[0];
    const remainingAmount = getRemainingAmount();

    setLocalPayoutPlan([
      ...localPayoutPlan,
      {
        bankAccount: firstBankAccount,
        amount: remainingAmount > 0 ? remainingAmount : 0,
      },
    ]);
  };

  const handleRemoveDistribution = (index: number) => {
    const newPlan = localPayoutPlan.filter((_, i) => i !== index);
    setLocalPayoutPlan(newPlan);
  };

  const handleBankAccountChange = (
    index: number,
    newBankAccount: BankAccount | null,
  ) => {
    if (!newBankAccount) return;

    const newPlan = [...localPayoutPlan];
    newPlan[index] = {
      ...newPlan[index],
      bankAccount: newBankAccount,
    };
    setLocalPayoutPlan(newPlan);
  };

  const handleAmountChange = (index: number, newAmount: string) => {
    const numericValue = Math.floor(parseFloat(newAmount) || 0);
    const newPlan = [...localPayoutPlan];
    newPlan[index] = {
      ...newPlan[index],
      amount: numericValue,
    };
    setLocalPayoutPlan(newPlan);
  };

  const getAvailableBankAccounts = (currentIndex: number) => {
    const selectedBankAccountIds = localPayoutPlan
      .map((dist, idx) => (idx !== currentIndex ? dist.bankAccount.id : null))
      .filter((id): id is number => id !== null);

    return selectableBankAccounts.filter(
      (account) =>
        !selectedBankAccountIds.includes(account.id) ||
        localPayoutPlan[currentIndex]?.bankAccount.id === account.id,
    );
  };

  const handleSave = () => {
    setPayoutPlan(localPayoutPlan);
    setOpen(false);
  };

  const handleClear = () => {
    setPayoutPlan([]);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const accumulatedAmount = getAccumulatedAmount();
  const remainingAmount = getRemainingAmount();

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Editar plan de giro</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
            }}
          >
            <Stack direction={'row'} justifyContent={'end'} marginBottom={2}>
              <IconButton
                onClick={handleAddDistribution}
                color="primary"
                size="medium"
                sx={{
                  color: 'primary.main',
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                    borderColor: 'grey.300',
                  },
                  '&:disabled': {
                    color: 'grey.400',
                    backgroundColor: 'grey.100',
                    borderColor: 'grey.200',
                  },
                  borderRadius: '100px',
                  padding: '4px 12px',
                }}
              >
                <AddIcon sx={{ fontSize: '1rem', paddingRight: '4px' }} />
                <Typography variant="caption" fontWeight={600}>
                  Agregar
                </Typography>
              </IconButton>
            </Stack>
            <Grid
              container
              spacing={2}
              justifyContent="flex-end"
              alignItems="center"
            >
              {localPayoutPlan.map((distribution, index) => (
                <>
                  <Grid
                    item
                    xs={1}
                    sx={{ textAlign: 'right' }}
                    alignItems={'center'}
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveDistribution(index)}
                      size="medium"
                    >
                      <Remove sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Grid>
                  <Grid item xs={8} key={index}>
                    <Autocomplete
                      options={getAvailableBankAccounts(index)}
                      value={distribution.bankAccount}
                      onChange={(_, newValue) =>
                        handleBankAccountChange(index, newValue)
                      }
                      getOptionLabel={(option) =>
                        `${option.bankName} - ${option.accountNumber} (${option.type})`
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cuenta bancaria"
                          size="small"
                          fullWidth
                        />
                      )}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Monto asignado"
                      type="number"
                      value={distribution.amount}
                      onChange={(e) =>
                        handleAmountChange(index, e.target.value)
                      }
                      size="small"
                      fullWidth
                      inputProps={{
                        min: 0,
                        step: 1,
                        sx: { textAlign: 'right' },
                      }}
                    />
                  </Grid>
                </>
              ))}
            </Grid>

            {localPayoutPlan.length === 0 && (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Agrega una cuenta bancaria para empezar a configurar el plan
                  de giro
                </Typography>
              </Box>
            )}
            <Grid
              container
              columnSpacing={2}
              rowSpacing={1}
              marginTop={2}
              alignItems="center"
            >
              <Grid item xs={6} />
              <Grid item xs={3} sx={{ textAlign: 'right' }}>
                <Typography
                  variant="body2"
                  color="secundary"
                  sx={{ margin: 0, lineHeight: 1.2 }}
                >
                  Monto total asignado:
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  variant="h5"
                  color={remainingAmount === 0 ? 'green' : 'text.secondary'}
                  fontWeight={remainingAmount === 0 ? 500 : 400}
                  sx={{ margin: 0, lineHeight: 1.2, textAlign: 'right' }}
                >
                  {formatMoney(accumulatedAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={3} sx={{ textAlign: 'right' }}>
                <Typography
                  variant="body2"
                  color="secundary"
                  sx={{ margin: 0, lineHeight: 1.2 }}
                >
                  Monto a girar:
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  variant="h5"
                  color="primary"
                  fontWeight={500}
                  sx={{ margin: 0, lineHeight: 1.2, textAlign: 'right' }}
                >
                  {formatMoney(operationToDeposit)}
                </Typography>
              </Grid>
              {remainingAmount !== 0 && (
                <>
                  <Grid item xs={6} />
                  <Grid item xs={3} sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="body2"
                      color="secundary"
                      sx={{ margin: 0, lineHeight: 1.2 }}
                    >
                      Monto restante:
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography
                      variant="h5"
                      color={remainingAmount > 0 ? 'text.secondary' : 'red'}
                      sx={{ margin: 0, lineHeight: 1.2, textAlign: 'right' }}
                    >
                      {formatMoney(remainingAmount)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleClose} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton onClick={handleClear} color="error">
          Vaciar plan
        </CustomButton>
        <CustomButton
          onClick={handleSave}
          disabled={remainingAmount !== 0}
          color="primary"
        >
          Guardar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditPayoutPlanDialog;
